import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getStadium, preloadStadium, PHONES_TEX_W } from '../stadium-assets.js';
import { vdjColor, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. La photo est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'stadium',
  family: 'photographic',
  danceability: 0.80,
  energyLevel: 0.76,
  bpmMin: 90,
  bpmMax: 140,
  movement: 0.60,
  tonality: 0.80,
  enabled: true
};

// Stadium : un stade de nuit vu depuis la pelouse, à partir de DEUX photos
// identiques, tout éteint et projecteurs allumés (tools/build-stadium.mjs,
// pipeline de NeonAlley, R8). Des milliers de lampes de téléphones
// s'allument dans les tribunes et font des vagues ; les projecteurs
// flashent sur les drops. Pop, hymnes électro, rock de stade, house vocale.
//
// LA PHOTO, une ligne par pixel, en lumière LINÉAIRE :
//     couleur = mix(off propre, on, I)
// (2026-10-17) Chaque rampe est un RECTANGLE ORIENTÉ aux bords doux (2 px)
// et un HALO séparé (masks.png) : rampe et halo suivent exactement le poids w
// de LEUR projecteur ; le reste (pelouse, brume, tribunes) le mélange des
// deux tours les plus proches. Dans le rectangle, l'image éteinte est une
// rampe sombre SYNTHÉTISÉE au build (cadre et grille de lentilles, reflets
// froids) : plus de tache grise. Allumage : 120 ms avec un scintillement de
// démarrage ; extinction : 300 ms, puis une braise ambrée sur les lentilles
// pendant 1 s. Jamais d'état gris prolongé. Toutes les rampes à 1 : l'image
// EST Stadium-on (au webp près, ?stadiumexact=1).
//
// LES TÉLÉPHONES : 6 000 points tirés au build DANS le masque des tribunes,
// lus dans une texture de données par le vertex shader (rien n'est envoyé
// par frame). Coeur de 1 px, halo de 3 px, blanc bleuté ; chacun balance
// sur sa propre phase. Ils s'allument de 0 à 100 % entre 5 et 30 % du
// morceau. La vague est une bande d'angle qui traverse le stade en 2 mesures.
//
// CONSTANTES MESURÉES (sur l'image finale, 1408 x 768) : rampes et tours
// par le build (stadium.<hash>.data.json) ; les faisceaux partent des
// rampes vers la pelouse, cible au pied du stade (BEAM_TARGET_V), demi-
// angle BEAM_HALF ; repos des projecteurs 15 %.

const REST = 0.15;                        // the stadium's ambient (haze, pitch), not a ramp's level
const ON_S = 0.12, OFF_S = 0.30, EMBER_S = 1.0;
const PHONES_T = { 0: 3000, 1: 6000, 2: 6000 };
const BEAMS_T = { 0: false, 1: true, 2: true };
const PHONE_FILL = [0.05, 0.30];          // progress where the phones come on
const PHONE_COLOR = [0.86, 0.92, 1.0];    // white, slightly blue
const WAVE_BARS = 2, WAVE_WIDTH = 0.07, WAVE_LIFT_PX = 2.5, WAVE_GAIN = 0.30;
const BEAT_TWINKLE = 0.05;
const SAFE_FLOOR = 0.55;
const BEAM_TARGET_V = 0.10;               // image v (from the bottom) the beams aim at
const BEAM_HALF = 0.30;                   // half-width at the target, in image u
const BEAM_GAIN = 0.16;
const DROP_BARS = 4, CHASE_BEATS = 4;
const BAR_PAIRS = [[0, 7], [2, 5], [1, 6], [3, 4]];   // two ramps a bar, symmetric
const NO_BPM = 120;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const ACCENT_FALLBACK = 0xff2bd6;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

const SAFE_FN = `
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAtt(vec2 s) {           // s: clip x scaled by aspect, clip y
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

// --- the photograph: three reads -----------------------------------------------
const photoVertex = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const photoFragment = `
  precision mediump float;
  uniform sampler2D uOff;
  uniform sampler2D uOn;
  uniform sampler2D uIds;
  uniform sampler2D uMasks;
  uniform sampler2D uMasksId;
  uniform float uEmber[8];
  uniform float uAmbient;
  uniform sampler2D uStands;
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  uniform float uRamp[8];
  uniform float uTower[4];
  uniform float uMask;
  varying vec2 vUv;
  float ramp(float id) {
    float v = 0.0;
    for (int k = 0; k < 8; k++) if (abs(float(k) - id) < 0.5) v = uRamp[k];
    return v;
  }
  float tower(float id) {
    float v = 0.0;
    for (int k = 0; k < 4; k++) if (abs(float(k) - id) < 0.5) v = uTower[k];
    return v;
  }
  void main() {
    vec2 uv = vUv * uUvScale + uUvOffset;
    vec3 off = texture2D(uOff, uv).rgb;
    vec3 on = texture2D(uOn, uv).rgb;
    vec4 id = texture2D(uIds, uv) * 255.0;
    vec4 m = texture2D(uMasks, uv);
    float rid = floor(texture2D(uMasksId, uv).b * 255.0 / 32.0);
    // the haze, the pitch, the stands: the two nearest towers
    float Ibg = mix(tower(id.r), tower(id.g), id.b / 255.0);
    // a ramp and its halo: EXACTLY its own floodlight (no remnant when it is off)
    // the two masks are complementary by construction (halo = (1 - rect) x ...):
    // their SUM is the ramp's share; a max left the soft edge half-owned, and
    // the towers' light showed through it as a bright outline
    float own = clamp(m.r + m.g, 0.0, 1.0);
    float wR = ramp(rid);
    float I = mix(Ibg, wR, own);
    vec3 col = mix(off, on, clamp(I, 0.0, 1.0));
    // the amber ember on the lenses, for a second after a floodlight goes out
    float em = 0.0;
    for (int k = 0; k < 8; k++) if (abs(float(k) - rid) < 0.5) em = uEmber[k];
    col += vec3(0.060, 0.022, 0.004) * m.a * m.r * em;
    if (uMask > 0.5) {
      // ?stadiummask=1: the stands in green, the ramps in their own hue
      float s = texture2D(uStands, uv).r;
      col = col * 0.5 + vec3(0.0, 0.45, 0.0) * s;
      if (m.r > 0.5) col = vec3(fract(rid * 0.37), fract(rid * 0.61 + 0.3), fract(rid * 0.83 + 0.6));
      col += vec3(0.4, 0.3, 0.0) * m.g;
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;

// --- the phones ---------------------------------------------------------------
const phoneVertex = `
  precision highp float;
  attribute float aIndex;
  uniform sampler2D uPhones;
  uniform vec2 uPhonesSize;
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  uniform vec2 uRes;
  uniform float uAspect;
  uniform float uTime;
  uniform float uFill;          // fraction of the phones lit
  uniform float uWaveC;         // wave centre in angle (0..1), far outside when idle
  uniform float uTwinkle;
  uniform mediump float uPx;    // point size in px (shared with the fragment shader)
  ${SAFE_FN}
  varying float vBright;
  varying float vSafe;
  // Dave Hoskins' hash11: no sin(), whose precision collapses for large
  // arguments on some GPUs (the sin hash returned exactly 0 for ~0.7 % of
  // the 6 000 indices, which lit them at 0 % fill)
  float hash(float p) { p = fract(p * 0.1031); p *= p + 33.33; p *= p + p; return fract(p); }
  void main() {
    vec2 tuv = vec2((mod(aIndex, uPhonesSize.x) + 0.5) / uPhonesSize.x, (floor(aIndex / uPhonesSize.x) + 0.5) / uPhonesSize.y);
    vec4 d = texture2D(uPhones, tuv);          // x, y (uv, y up), row, angle
    // image uv -> screen uv (cover fit)
    vec2 s = (d.xy - uUvOffset) / uUvScale;
    vec2 clip = s * 2.0 - 1.0;
    float h = hash(aIndex);
    float lit = h < uFill ? 1.0 : 0.0;
    // the arm: a slow sway on its own phase, under 1.5 px
    float ph = hash(aIndex + 7.1) * 6.2832, f = 0.35 + 0.5 * hash(aIndex + 3.3);
    vec2 sway = vec2(sin(uTime * f + ph), 0.5 * sin(uTime * f * 1.7 + ph * 1.3)) * 1.3;
    // the wave: a band of phones raised, +30 %, a little higher
    float w = exp(-pow((d.w - uWaveC) / ${WAVE_WIDTH.toFixed(3)}, 2.0));
    sway.y += w * ${WAVE_LIFT_PX.toFixed(1)};
    clip += sway * 2.0 / uRes;
    vBright = lit * (1.0 + ${WAVE_GAIN.toFixed(2)} * w) * (1.0 + uTwinkle * (0.5 + 0.5 * hash(aIndex + 1.9))) * (0.35 + 0.65 * pow(hash(aIndex + 5.5), 1.5));   // screens at every angle and distance
    vSafe = safeAtt(vec2(clip.x * uAspect, clip.y));
    gl_Position = vec4(clip, 0.0, lit > 0.5 ? 1.0 : -1.0);   // unlit: behind the camera, clipped
    gl_PointSize = uPx;
  }
`;
const phoneFragment = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uPx;
  varying float vBright;
  varying float vSafe;
  void main() {
    // distance from the point's centre in px: a 1 px core, a 3 px halo
    float dPx = length(gl_PointCoord - 0.5) * uPx;
    float core = 1.0 - smoothstep(0.5, 1.0, dPx);
    // 6 000 halos overlap into a white carpet at 0.30: 0.12 keeps the seats readable
    float halo = exp(-max(dPx - 0.5, 0.0) / 1.2) * 0.12 * (1.0 - smoothstep(2.5, 3.5, dPx));
    float a = (core * 0.95 + halo) * vBright * vSafe;
    if (a < 0.003) discard;
    gl_FragColor = vec4(uColor * a, 1.0);
  }
`;

// --- the beams: one quad per ramp, additive, soft ---------------------------------------
const beamVertex = `
  precision highp float;
  attribute vec2 aCorner;       // x across -1..1, y along 0..1
  attribute float aRamp;
  attribute vec4 aGeom;         // lamp uv (x, y up), target uv (x, y up)
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  uniform float uAspect;
  uniform float uRamp[8];
  ${SAFE_FN}
  varying vec2 vLocal;
  varying float vI;
  varying float vSafe;
  void main() {
    vec2 a = (aGeom.xy - uUvOffset) / uUvScale * 2.0 - 1.0;
    vec2 b = (aGeom.zw - uUvOffset) / uUvScale * 2.0 - 1.0;
    vec2 dir = b - a;
    vec2 nrm = normalize(vec2(-dir.y * uAspect, dir.x / uAspect));
    float halfW = ${BEAM_HALF.toFixed(2)} / uUvScale.x * 2.0;
    vec2 p = a + dir * aCorner.y + vec2(nrm.x / uAspect, nrm.y) * aCorner.x * halfW * (0.04 + aCorner.y);
    vLocal = aCorner;
    float I = 0.0;
    for (int k = 0; k < 8; k++) if (abs(float(k) - aRamp) < 0.5) I = uRamp[k];
    vI = I;
    vSafe = safeAtt(vec2(p.x * uAspect, p.y));
    gl_Position = vec4(p, 0.0, 1.0);
  }
`;
const beamFragment = `
  precision mediump float;
  uniform float uGain;
  varying vec2 vLocal;
  varying float vI;
  varying float vSafe;
  void main() {
    float along = vLocal.y;
    float across = vLocal.x;
    float a = pow(1.0 - along, 1.6) * exp(-across * across * 3.0) * smoothstep(0.0, 0.05, along);
    // the beams follow their floodlight's weight (0 off, 1 on)
    float k = vI;
    gl_FragColor = vec4(vec3(0.80, 0.88, 1.0) * a * k * uGain * vSafe, 1.0);
  }
`;

export class StadiumVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._debugMask = urlParam('stadiummask') === '1';
    this._debugWave = urlParam('stadiumwave') === '1';
    const f = parseFloat(urlParam('stadiumphones'));
    this._forcePhones = Number.isFinite(f) ? clamp01(f) : null;
    this._exact = urlParam('stadiumexact') === '1';
    this._allOff = urlParam('stadiumoff') === '1';      // every floodlight held off (checks of the off look)
    this._comfort = new VdjComfort();
    this._post = this._exact
      ? { bloomStrength: 0, vignette: 0, grain: 0, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 }
      : { bloomStrength: 0.45, bloomThreshold: 0.80, vignette: 0.30, grain: 0.012, aberration: 0.0012, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
  }

  get postProcessing() { return this._post; }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    const assets = getStadium();
    this._assets = assets;
    this._ready = !!assets;
    if (!assets) {
      // a forced start before the preload landed: rebuild once it arrives
      const self = this;
      preloadStadium().then(function (ok) {
        if (!ok || !self._scene || self._assets) return;
        const s = self._scene, c = self._camera;
        self.dispose(); self.init(s, c);
      });
      return;
    }
    const data = assets.data;
    this._imageAspect = data.size[0] / data.size[1];
    this._ramps = data.ramps;
    this._nRamps = Math.min(8, data.ramps.length);
    this._towerOfRamp = data.ramps.map((r) => r.tower);
    this._nTowers = data.towers.length;

    this._rampI = new Array(8).fill(0);
    this._rampW = new Array(8).fill(0); this._rampT = new Array(8).fill(0);
    this._onAt = new Array(8).fill(-1e9); this._offAt = new Array(8).fill(-1e9);
    this._towerI = new Array(4).fill(REST);
    this._uUvScale = { value: new THREE.Vector2(1, 1) };
    this._uUvOffset = { value: new THREE.Vector2(0, 0) };
    this._uRamp = { value: this._rampI.slice() };
    const safe = { uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };

    // the photograph
    this._photoU = {
      uOff: { value: assets.off }, uOn: { value: assets.on }, uIds: { value: assets.ids }, uMasks: { value: assets.masks }, uMasksId: { value: assets.masksId }, uStands: { value: assets.stands },
      uEmber: { value: new Array(8).fill(0) }, uAmbient: { value: REST },
      uUvScale: this._uUvScale, uUvOffset: this._uUvOffset, uRamp: this._uRamp, uTower: { value: this._towerI.slice() },
      uMask: { value: this._debugMask ? 1 : 0 }
    };
    this._photo = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._photoU, vertexShader: photoVertex, fragmentShader: photoFragment, depthTest: false, depthWrite: false }));
    this._photo.frustumCulled = false; this._photo.renderOrder = -3000;
    scene.add(this._photo);

    // the phones: one Points, the index is the only attribute (static)
    const n = Math.min(PHONES_T[this._tier] || 6000, assets.phoneCount);
    this._nPhones = n;
    const idx = new Float32Array(n);
    // tier 0 keeps every other phone: the same spread, half the count
    const stride = assets.phoneCount / n;
    for (let i = 0; i < n; i++) idx[i] = Math.floor(i * stride);
    const pg = new THREE.BufferGeometry();
    pg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    pg.setAttribute('aIndex', new THREE.BufferAttribute(idx, 1));
    this._phoneU = Object.assign({
      uPhones: { value: assets.phones }, uPhonesSize: { value: new THREE.Vector2(PHONES_TEX_W, assets.phones.image.height) },
      uUvScale: this._uUvScale, uUvOffset: this._uUvOffset, uRes: { value: new THREE.Vector2(1280, 720) }, uAspect: { value: 1.78 },
      uTime: { value: 0 }, uFill: { value: 0 }, uWaveC: { value: -9 }, uTwinkle: { value: 0 }, uPx: { value: 7 },
      uColor: { value: new THREE.Color(PHONE_COLOR[0], PHONE_COLOR[1], PHONE_COLOR[2]) }
    }, safe);
    this._phones = new THREE.Points(pg, new THREE.ShaderMaterial({
      uniforms: this._phoneU, vertexShader: phoneVertex, fragmentShader: phoneFragment,
      transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false }));
    this._phones.frustumCulled = false; this._phones.renderOrder = -2800;
    scene.add(this._phones);

    // the beams (not at tier 0)
    if (BEAMS_T[this._tier] && !this._exact) {
      const R = this._nRamps;
      const corner = new Float32Array(R * 4 * 2), rampA = new Float32Array(R * 4), geom = new Float32Array(R * 4 * 4), index = [];
      const C = [[-1, 0], [1, 0], [-1, 1], [1, 1]];
      for (let r = 0; r < R; r++) {
        const rp = this._ramps[r];
        const lx = rp.cx, ly = 1 - rp.cy;
        // aim: down to the pitch, leaning a little towards the centre
        const tx = lx + (0.5 - lx) * 0.25, ty = BEAM_TARGET_V;
        for (let c = 0; c < 4; c++) {
          const v = r * 4 + c;
          corner[v * 2] = C[c][0]; corner[v * 2 + 1] = C[c][1];
          rampA[v] = r;
          geom[v * 4] = lx; geom[v * 4 + 1] = ly; geom[v * 4 + 2] = tx; geom[v * 4 + 3] = ty;
        }
        index.push(r * 4, r * 4 + 1, r * 4 + 2, r * 4 + 2, r * 4 + 1, r * 4 + 3);
      }
      const bg = new THREE.BufferGeometry();
      bg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(R * 4 * 3), 3));
      bg.setAttribute('aCorner', new THREE.BufferAttribute(corner, 2));
      bg.setAttribute('aRamp', new THREE.BufferAttribute(rampA, 1));
      bg.setAttribute('aGeom', new THREE.BufferAttribute(geom, 4));
      bg.setIndex(index);
      this._beamU = Object.assign({ uUvScale: this._uUvScale, uUvOffset: this._uUvOffset, uAspect: this._phoneU.uAspect, uRamp: this._uRamp, uGain: { value: BEAM_GAIN } }, safe);
      this._beams = new THREE.Mesh(bg, new THREE.ShaderMaterial({
        uniforms: this._beamU, vertexShader: beamVertex, fragmentShader: beamFragment,
        transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false, side: THREE.DoubleSide }));
      this._beams.frustumCulled = false; this._beams.renderOrder = -2900;
      scene.add(this._beams);
    }
    const self = this;
    this._size = new THREE.Vector2();
    this._photo.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(self._size);
      self._phoneU.uRes.value.copy(self._size);
    };

    // state
    this._time = 0; this._beats = 0; this._bars = 0; this._phrases = 0;
    this._phaseBeat = null; this._phaseBar = null; this._phrasePhaseSeen = null;
    this._free = 0; this._freeBeat = 0; this._secondsBeat = 0.5;
    this._waveStart = -1e9; this._pairUntil = -1; this._pair = null; this._pairTurn = 0;
    this._dropAt = -1e9; this._dropUntil = -1e9;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._paletteSeen = undefined; this._accent = vdjColor(ACCENT_FALLBACK);
    this._aspect = 0;
    this._reframe();
  }

  // cover fit of the picture to the live aspect
  _reframe() {
    const aspect = safeZoneUniforms.uAspect.value || 16 / 9;
    this._aspect = aspect;
    this._phoneU.uAspect.value = aspect;
    if (aspect > this._imageAspect) {
      const k = this._imageAspect / aspect;
      this._uUvScale.value.set(1, k); this._uUvOffset.value.set(0, (1 - k) / 2);
    } else {
      const k = aspect / this._imageAspect;
      this._uUvScale.value.set(k, 1); this._uUvOffset.value.set((1 - k) / 2, 0);
    }
  }

  _applyPalette(profile) {
    if (profile.artworkPalette === this._paletteSeen) return;
    this._paletteSeen = profile.artworkPalette;
    const v = profile.artworkPalette && profile.artworkPalette.vividColors;
    this._accent = vdjColor(v && v.length ? v[0] : ACCENT_FALLBACK);
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { if (this._ready) { this._applyPalette(profile); this._dropIdx = 0; this._lastNowMs = null; } }

  update(deltaTime, profile) {
    if (!this._ready) return;
    const aspect = safeZoneUniforms.uAspect.value || 0;
    if (aspect && aspect !== this._aspect) this._reframe();
    // pause: everything frozen where it is - never black
    if (profile.playbackState !== 'PLAYING') return;
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    this._applyPalette(profile);
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase, ph = profile.phrasePhase;
    const grid = bp != null && bpm > 0;
    this._secondsBeat = grid ? 60 / bpm : 60 / NO_BPM;
    const beatS = this._secondsBeat, barS = 4 * beatS;

    let beat = false, bar = false, phrase = false;
    if (grid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true;
      this._phaseBeat = bp;
      if (barIn != null) { if (this._phaseBar != null && barIn < this._phaseBar - 0.5) bar = true; this._phaseBar = barIn; }
      else if (beat && (this._beats + 1) % 4 === 0) bar = true;
      if (ph != null) { if (this._phrasePhaseSeen != null && ph < this._phrasePhaseSeen - 0.5) phrase = true; this._phrasePhaseSeen = ph; }
      else if (bar && (this._bars + 1) % 4 === 0) phrase = true;
    } else {
      // no BPM: a 120 BPM clock
      this._free += dt;
      if (this._free >= beatS) { this._free -= beatS; beat = true; this._freeBeat = (this._freeBeat + 1) % 4; if (this._freeBeat === 0) bar = true; }
      if (bar && (this._bars + 1) % 4 === 0) phrase = true;
    }
    if (beat) this._beats++;
    if (bar) this._bars++;
    if (phrase) this._phrases++;
    const beatPhase = grid ? bp : this._free / beatS;

    // PHRASE: the wave (2 bars). ?stadiumwave=1: one wave every 2 bars.
    if (phrase || (this._debugWave && this._time - this._waveStart >= WAVE_BARS * barS)) this._waveStart = this._time;
    // BAR: two ramps for half a bar
    if (bar) {
      this._pair = BAR_PAIRS[this._pairTurn % BAR_PAIRS.length];
      this._pairTurn++;
      this._pairUntil = this._time + barS / 2;
    }
    // DROP: chase, then full; flash; the phones in the accent for 4 bars
    if (grid && this._tickDrops(profile)) {
      this._dropAt = this._time;
      this._dropUntil = this._time + DROP_BARS * barS;
      if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
    }

    // ramp TARGETS: a floodlight is on or off - never parked half-lit (the
    // old 15 % rest, the 25 % floor of the chase and the one-bar fade back all
    // showed the grey state)
    const tgt = this._rampT;
    for (let r = 0; r < 8; r++) tgt[r] = 0;
    if (this._time < this._pairUntil && this._pair) for (const r of this._pair) if (r < this._nRamps) tgt[r] = 1;
    const sinceDrop = this._time - this._dropAt;
    if (this._time < this._dropUntil) {
      const chaseS = CHASE_BEATS * beatS;
      if (sinceDrop < chaseS) {
        // the chase: one ramp after the other, left to right
        const k = Math.floor(sinceDrop / chaseS * this._nRamps * 2) % this._nRamps;
        tgt[this._chaseOrder()[k]] = 1;
      } else for (let r = 0; r < this._nRamps; r++) tgt[r] = 1;      // full until the end of the drop
    }
    if (this._exact) for (let r = 0; r < 8; r++) tgt[r] = 1;
    if (this._allOff) for (let r = 0; r < 8; r++) tgt[r] = 0;
    // ENVELOPES: on in 120 ms with a start-up flicker; off in 300 ms, then an
    // amber ember on the lenses for 1 s
    const W = this._rampI, E = this._photoU.uEmber.value;
    for (let r = 0; r < 8; r++) {
      const w0 = this._rampW[r];
      let w = w0;
      if (tgt[r] > w) {
        if (w <= 0.001) this._onAt[r] = this._time;
        w = Math.min(1, w + dt / ON_S);
      } else if (tgt[r] < w) {
        if (w0 >= 0.999) this._offAt[r] = this._time;
        w = Math.max(0, w - dt / OFF_S);
      }
      this._rampW[r] = w;
      // the flicker of a lamp striking: two dips in the first 120 ms
      const sinceOn = this._time - this._onAt[r];
      const flick = tgt[r] > 0 && sinceOn < ON_S ? 1 - 0.35 * Math.max(0, Math.sin(sinceOn / ON_S * Math.PI * 4)) : 1;
      // off: eased (the lamp dims fast, then the last of it)
      const shaped = tgt[r] >= w ? w * flick : w * w * (3 - 2 * w);
      W[r] = this._exact ? 1 : shaped;
      const sinceOff = this._time - this._offAt[r];
      E[r] = tgt[r] < 0.5 && sinceOff >= 0 && sinceOff < EMBER_S ? Math.exp(-sinceOff / (EMBER_S / 3)) * (1 - w) : 0;
    }
    for (let r = 0; r < 8; r++) this._uRamp.value[r] = W[r];
    // towers: the ambient of the stadium (REST) plus the mean of their ramps,
    // for the haze, the pitch and the stands - never for a ramp or its halo
    const T = this._photoU.uTower.value;
    for (let t = 0; t < 4; t++) { let s = 0, n = 0; for (let r = 0; r < this._nRamps; r++) if (this._towerOfRamp[r] === t) { s += W[r]; n++; } T[t] = REST + (1 - REST) * (n ? s / n : 0); }

    // phones
    const U = this._phoneU;
    U.uTime.value = this._time;
    const progress = profile.progress != null ? profile.progress : (profile.duration ? (profile.estimatedPosition || 0) / profile.duration : 0.5);
    const fill = clamp01((progress - PHONE_FILL[0]) / (PHONE_FILL[1] - PHONE_FILL[0]));
    U.uFill.value = this._exact ? 0 : (this._forcePhones != null ? this._forcePhones : fill);
    const wt = (this._time - this._waveStart) / (WAVE_BARS * barS);
    U.uWaveC.value = wt >= 0 && wt <= 1 ? -WAVE_WIDTH * 2 + wt * (1 + WAVE_WIDTH * 4) : -9;
    U.uTwinkle.value = BEAT_TWINKLE * Math.exp(-beatPhase * 5);
    const accent = this._time < this._dropUntil ? 1 : 0;
    U.uColor.value.setRGB(PHONE_COLOR[0], PHONE_COLOR[1], PHONE_COLOR[2]).lerp(this._accent, accent);
  }

  _chaseOrder() {
    if (!this._order) this._order = this._ramps.map((r, i) => i).sort((a, b) => this._ramps[a].cx - this._ramps[b].cx || this._ramps[a].cy - this._ramps[b].cy);
    return this._order;
  }

  dispose() {
    for (const o of [this._photo, this._phones, this._beams]) {
      if (!o) continue;
      o.onBeforeRender = null;
      if (this._scene) this._scene.remove(o);
      o.geometry.dispose(); o.material.dispose();
    }
    this._photo = this._phones = this._beams = null;
    this._scene = null;
  }
}
